import {
  AppBar, CssBaseline, Toolbar, useMediaQuery, useTheme,
} from '@mui/material';
import { IconChevronRight } from '@tabler/icons';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import Breadcrumbs from 'components/ui/Breadcrumbs';
import LinearLoader from 'components/ui/LinearLoader';
import { setIsSidebarOpen } from 'store/customizationSlice';
import { MenuCampaignsSelectors } from 'store/menuCampaignsSlice';
import { drawerWidth } from 'themes';

import Header from './Header';
import menuItems from './MenuItems';
import Sidebar from './Sidebar';

const toolbarSX = {
  background: process.env.REACT_APP_ENV !== 'prod' ? '#f7aeae' : 'white'
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex'
  },
  appBar: {
    backgroundColor: theme.palette.background.default
  },
  appBarWidth: {
    transition: theme.transitions.create('width'),
    backgroundColor: theme.palette.background.default
  },
  content: {
    ...theme.typography.mainContent,
    display: 'flex',
    flexDirection: 'column',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    [theme.breakpoints.up('md')]: {
      marginLeft: -(drawerWidth - 20),
      width: `calc(100% - ${drawerWidth}px)`
    },
    [theme.breakpoints.down('md')]: {
      marginLeft: '20px',
      width: `calc(100% - ${drawerWidth}px)`,
      padding: '16px'
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '10px',
      width: `calc(100% - ${drawerWidth}px)`,
      padding: '16px',
      marginRight: '10px'
    }
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    [theme.breakpoints.down('md')]: {
      marginLeft: '20px'
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: '10px'
    }
  }
}));

const MainLayout = () => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();

  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const isSidebarOpen = useSelector((state) => state.customization.isSidebarOpen);
  const pendingCampaigns = useSelector(MenuCampaignsSelectors.allPending);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  const navigation = menuItems(pendingCampaigns);

  const handleLeftDrawerToggle = () => {
    dispatch(setIsSidebarOpen(!isSidebarOpen));
  };

  useEffect(() => {
    dispatch(setIsSidebarOpen(!matchDownMd));
  }, [matchDownMd]);

  if (!isLoggedIn) return <LinearLoader />;

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        className={isSidebarOpen ? classes.appBarWidth : classes.appBar}>
        <Toolbar sx={toolbarSX}>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>
      <Sidebar drawerOpen={isSidebarOpen} drawerToggle={handleLeftDrawerToggle} />
      <main
        className={cx([
          classes.content,
          {
            [classes.contentShift]: isSidebarOpen,
          },
        ])}
      >
        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
        <Outlet />
      </main>
    </div>
  );
};

export default MainLayout;
